<template>
  <div
    class="search-bar relative"
    :class="{ '--search-bar-spain': isSpain, 'is-focus': searchBarIsFocus }"
  >
    <form
      method="GET"
      class="form bb-mr-5"
      :action="$router.resolve({ name: 'products' }).href"
      @submit.prevent="searchSubmitted()"
      :class="{ 'bb-ml-6': isSpain }"
    >
      <div
        class="bb-control search-bar-container bb-row bb-justify-content-end"
        :class="{
          '--spainWidth': isSpain && (!searchBarIsFocus || $screen.width > 991),
        }"
      >
        <input
          type="search"
          autocomplete="off"
          name="keywords"
          aria-label="search-text"
          v-model="searchValue"
          ref="searchInput"
          :placeholder="placeholder"
          class="bb-input search-input"
          :class="{ 'figtree-font': isSpain }"
          @input="event => (searchValue = event.target.value)"
          @focus="openOptions()"
        />
        <button
          type="submit"
          class="btn-search"
          :class="{
            '--top-margin-hidden': $screen.width < 991 && searchBarIsFocus,
          }"
          ref="btnSearch"
          @click="openOptions()"
          aria-label="search-button"
        >
          <span
            class="btn-search-icon"
            :class="{ '--icon-focus-color': searchBarIsFocus }"
            v-html="searchIcon"
          ></span>
        </button>
      </div>
      <div
        v-html="closeIcon"
        v-if="searchBarIsFocus"
        @click="closeOptions()"
        class="--close-icon bb-hidden-lg"
      ></div>
      <search-options
        v-if="searchBarIsFocus"
        :isPredictive="isPredictive"
        :suggestionsResults="suggestionsResults"
        :searchValue="searchValue"
        :allSearchResults="allSearchResults"
        :loading="isLoading"
        :isSpain="isSpain"
        :containBoxesFlag="containBoxesFlag"
        @searchSubmitted="suggestionSearch(searchValue)"
        @suggestionSearch="suggestionSearch($event)"
        @closeOptions="closeOptions"
      ></search-options>
    </form>
    <div
      @click="closeOptions('background')"
      v-if="searchBarIsFocus"
      class="black-background fixed"
    ></div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import SearchOptions from 'components/SearchOptions/SearchOptions.vue';
  import { sendAnalyticsEvent } from 'common/utils/sendAnalyticsEvent';
  import getLastTld from 'common/services/utils/getLastTld';
  import searchIcon from 'common/icons/Search.svg';
  import closeGrayIcon from 'assets/icons/close_gray.svg';

  import ElasticSearchEmpty from 'assets/graphql/queries/ElasticSearchEmpty.graphql';
  import PredictiveElasticSearch from 'assets/graphql/queries/PredictiveElasticSearch.graphql';
  import useAccount from 'composables/useAccount';
  import useFilters from 'composables/useFilters';
  import { Watch } from 'vue-property-decorator';
  import useIsMobile from 'common/composables/useIsMobile.js';
  import useKustomerBotHelpers from 'common/composables/kustomerBotHelpers';
  import {
    INITIAL_STATE_BOX,
    INITIAL_STATE_ACTIVITIES,
    INITIAL_STATE_TRAVEL_UY,
  } from 'constants/initialState';

  @Component({
    components: {
      SearchOptions,
    },
    props: {
      placeholder: {
        type: String,
        required: true,
      },
      user: {
        type: Object,
      },
    },
    apollo: {
      elastic_search_empty: {
        query: ElasticSearchEmpty,
        result({ data }) {
          this.suggestionsResults = data.elastic_search_empty.suggestions;
          this.boxSearchResults = data.elastic_search_empty.boxes;
        },
      },
    },
    watch: {
      user(user) {
        if (!user) return;

        const isBigboxPlus = !!user?.is_bigbox_plus;
        const elBtnSearch = this.$refs.btnSearch;

        // Run Bigbox plus animation
        if (isBigboxPlus) {
          elBtnSearch.classList.add('bb-plus-hide-search-animation');
          setTimeout(() => {
            elBtnSearch.classList.remove('bb-plus-hide-search-animation');
          }, 6100);
        }
      },
      searchBarIsFocus(newVal) {
        if (newVal && this.isMobile) {
          this.hideKustomer();
        } else if (!this.isMobile) {
          this.showKustomer();
        } else {
          this.showKustomer();
        }
      },
    },
    setup() {
      const { getUserData } = useAccount();
      const { clearCurrentFilters, setKeywords } = useFilters();
      const { isMobile } = useIsMobile();
      const { hideKustomer, showKustomer } = useKustomerBotHelpers();

      return {
        hideKustomer,
        showKustomer,
        isMobile,
        clearCurrentFilters,
        setKeywords,
        getUserData,
      };
    },
  })
  export default class SearchBarComponent extends Vue {
    searchValue = '';
    isSpain = getLastTld() === 'es';
    searchIcon = searchIcon;
    closeIcon = closeGrayIcon;
    searchBarIsFocus = false;
    isLoading = false;

    boxSearchResults = '';
    suggestionsResults = '';
    isPredictive = false;
    containBoxesFlag = null;
    allSearchResults = [];
    debounceTimeout = null;

    searchSubmitted() {
      if (!this.searchValue) return;
      sendAnalyticsEvent('search', undefined, {
        searchTerm: this.searchValue,
        userData: {
          email: this.email,
        },
      });

      this.redirectToProducts(this.searchValue);
      this.searchValue = '';
      this.closeOptions();
    }

    async suggestionSearch(suggestion) {
      this.searchValue = suggestion;
      this.searchSubmitted();
      this.closeOptions();
      await this.predictiveSearch(suggestion);
    }

    redirectToProducts(keywords, filters = {}) {
      // See onCardOnly in Filters.component.js and
      // header routes in BaseHeader.component.js

      // Determinar el tipo de card basado en containBoxesFlag
      const cardType = this.containBoxesFlag ? 0 : 1;

      const currentFilters = [
        { ...INITIAL_STATE_BOX }, // BOX filters (índice 0)
        { ...INITIAL_STATE_ACTIVITIES }, // ACTIVITY filters (índice 1)
        { ...INITIAL_STATE_TRAVEL_UY }, // TRAVEL filters (índice 2)
      ];

      const queryParams = {
        card: cardType,
        currentFilters: JSON.stringify(currentFilters),
      };

      // Agregar keywords si existe
      if (keywords) {
        queryParams.keywords = keywords;
      }

      const location = this.$router.resolve({
        name: 'products',
        query: queryParams,
      });

      window.location.href = location.href;
      /*
      this.$router.push({
        name: 'products',
        query: {
          card: this.containBoxesFlag ? 0 : 1,
          keywords,
        },
      });
      */
    }

    replaceRouter(keywords) {
      this.$router.replace({
        name: 'products',
        query: { ...this.$route.query, keywords },
      });
    }

    /**
     * It set boxSearchResults and activitiesSearchResults obtained form elastic search
     * If its mobile version it slices the arrays
     * If param value is empty executes the empty elastic query
     */
    @Watch('searchValue')
    async predictiveSearch() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      if (this.searchValue === '') {
        // Solo limpiar keywords en el store si estamos en la vista de filtros
        if (this.$route.name === 'products') {
          this.setKeywords('', false, false);
        }
        this.isPredictive = false;
        await this.$apollo.queries.elastic_search_empty.refetch();
      } else {
        this.debounceTimeout = setTimeout(async () => {
          this.isLoading = true;
          this.containBoxesFlag = null;
          this.isPredictive = true;
          const { data } = await this.$apollo.query({
            query: PredictiveElasticSearch,
            variables: {
              query: this.searchValue,
            },
          });
          this.allSearchResults = data.predictive_elastic_search;
          this.containBoxesFlag = data.predictive_elastic_search.some(result =>
            Boolean(result.box),
          );

          this.isLoading = false;
        }, 500);
      }
    }

    openOptions() {
      this.searchBarIsFocus = true;
      this.removeScroll();
      this.focusInputText();
    }

    closeOptions(context = 'other') {
      this.searchValue = '';
      if (context === 'other' || (context === 'background' && !this.isMobile)) {
        this.searchBarIsFocus = false;
        this.addScroll();
      }
    }
    removeScroll() {
      /**
       * Don't change the order or it won't work
       */
      window.document.body.style.top = `-${window.scrollY}px`;
      window.document.body.style.position = 'fixed';
    }

    addScroll() {
      const scrollY = window.document.body.style.top;
      window.document.body.style.position = '';
      window.document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }

    focusInputText() {
      setTimeout(
        () => {
          this.$refs.searchInput.focus();
        },
        this.isMobile ? 100 : 0,
      );
    }

    get isMobile() {
      return this.$screen.width < 768;
    }

    get email() {
      return this.getUserData?.email || '';
    }
  }
</script>

<style lang="scss" scoped>
  @import '~assets/styles/index.scss';

  .search-bar {
    @media (max-width: $screen-md-min) {
      text-align: right;
      width: 75%;
      transition: width 0.25s ease-in-out;

      @media (max-width: $screen-sm-min) {
        width: 60%;
      }

      @media (max-width: $screen-xs-min) {
        width: 30%;
      }
    }

    .form {
      z-index: $HEADER-Z-INDEX + 3;
      display: inline-flex;
      position: relative;

      @media (max-width: $screen-sm-max) {
        flex-direction: row-reverse;
        margin-right: 0;
        margin-left: auto;
        width: auto;
        padding-right: 0px;
      }

      .search-bar-container {
        width: 344px;
        height: 40px;

        &.--spainWidth {
          width: 250px;
        }

        @media (max-width: $screen-sm-max) {
          width: 100%;
        }

        @media (max-width: $screen-md-min) {
          border-radius: 50%;

          .search-input {
            display: none;
          }

          .btn-search {
            &.--top-margin-hidden {
              margin-top: 0px;
            }
            width: 16px;
            height: 16px;
            position: relative;
            .btn-search-icon {
              height: 16px;
              width: 16px;
              color: #222222;
              &.--icon-focus-color {
                color: #5858da;
              }
            }
          }
        }
        .btn-search {
          width: 32px;
          height: 100%;
          background-color: transparent;
          border: none;
          position: absolute;
          top: 0px;
          right: 16px;
          display: flex;
          align-items: center;
          margin-top: 3px;
          animation-duration: 4s;
          animation-delay: 2s;
          animation-direction: alternate;
          animation-iteration-count: 1;
          animation-fill-mode: forwards;

          &:hover {
            cursor: pointer;
          }

          .btn-search-icon {
            color: #222222;
            &.--icon-focus-color {
              color: #5858da;
            }
          }
          &:deep(svg) {
            height: 18px;
            @media (max-width: $screen-md-min) {
              height: 16px;
            }
          }

          @media (max-width: $screen-md-min) {
            padding: $MINI;
          }
        }
        input {
          height: 40px;
          width: 550px;
          border-radius: 10px;
          border-color: $GRAY-LIGHT;
          font-size: 16px;
          min-height: 0;
          padding: 11px 55px 11px 15px;
          transition: width 0.3s;
          color: #222222;

          @media (max-width: $screen-md-max) {
            width: 400px;
          }

          @media (max-width: $screen-sm-max) {
            width: 100%;
          }

          @media (max-width: $screen-xs-min) {
            padding: 0 $MINI;
          }
          &:focus {
            border-color: #5858da;
            box-shadow: none;
          }
          &::placeholder {
            color: $MID-GRAY;

            @media (max-width: $screen-xs-min) {
              font-size: 14px;
            }
          }
        }
      }
    }

    &.--search-bar-spain {
      @media (max-width: $screen-md-min) {
        opacity: 0;
      }

      .form {
        margin-right: 0px;

        input {
          font-size: 16px;
          padding: 10px 55px 13px 18px;
        }

        .search-bar-container {
          .btn-search {
            justify-content: center;

            & svg {
              color: $DARK-GRAY;
            }
          }
        }
      }
    }

    &.is-focus {
      @media (max-width: $screen-md-min) {
        position: absolute;
        width: 100%;
        padding: 0 10px;
        right: 0;
        z-index: 2;
        top: 14px;
        opacity: 1;

        .form {
          width: 100%;
          display: flex;
          flex-direction: row;
          align-items: center;
          .--close-icon {
            width: 14px;
            height: 14px;
          }
          .search-bar-container {
            width: 92%;
            margin-right: 12px;
            left: 0;
            align-self: flex-start;
          }

          @media (max-width: $screen-md-min) {
            .search-input {
              display: block;
            }
          }
        }

        .bb-input {
          height: 48px;
          &:focus {
            box-shadow: none;
            border: solid 1px #5858da;
          }
        }
      }

      .btn-search {
        @media (max-width: $screen-md-min) {
          margin-left: 79vw;
        }
      }
    }

    .black-background {
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      z-index: $HEADER-Z-INDEX + 2;
      @media screen and (max-width: $screen-md-min) {
        background-color: white;
      }
    }
  }

  .bb-plus-hide-search-animation {
    animation-name: bb-plus-hide-search-anim;
    pointer-events: none;
  }

  @keyframes bb-plus-hide-search-anim {
    0% {
      opacity: 1;
    }
    5% {
      opacity: 0;
    }

    // keep tag

    // hide tag
    95% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
</style>
